import { useGetIdentity, useNotify, useRedirect, Title } from 'react-admin';
import { useParams } from 'react-router-dom';
import { adsProxy, aiProxy, customReasonsProxy, legitAiSummariesProxy } from './apiGateway';
import { Box, Typography, Card, CardContent, Button, Stack, Skeleton, Modal, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio, TextField, Alert, Checkbox, Chip, CircularProgress, Popover, Tooltip, Link as MuiLink } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { useState, useEffect, useMemo, useRef } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PersonIcon from '@mui/icons-material/Person';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { DEFAULT_EXPERT_REASONS, ReasonGroup, ReasonType } from './reasonsConfig';
import { useAdScreenLock } from './useAdScreenLock';
import { adLocksApi, getAdLockClientId } from './adLocks';
import { canOpenPlatformUserShow, getPlatformUserShowUrl } from './platformUserShowLink';
import { AiModerationResultCard, type AiModeration } from './AiModerationResultCard';


interface AdInfo {
    gender: string;
    category: string;
    subcategory: string;
    attachmentPhotoIds: string[];
    clothStatus: string;
    brand: string;
    model: string;
    size: string;
    country: string;
    fullPrice: number;
    city: string;
    isExchange: boolean;
    isVintage: boolean;
    isExistBox: boolean;
    description: string;
    serialNumber?: string;
}

interface UserInfo {
    id: string;
    name: string;
    rating: number;
    reviewsCount: number;
    countActiveAds: number;
    isActive: boolean;
    avatarUrl?: string;
}

interface ModerationAdDetail {
    id: string;
    info: AdInfo;
    currentStatus: string;
    statusHistory: string[];
    creationDate: string;
    lastUpdateDate: string;
    userInfo: UserInfo;
    expertModerationExtraInfos?: ExpertModerationExtraInfo[];
    aiModeration?: AiModeration | null;
}

interface ExpertModerationExtraInfo {
    photosUrl: string[];
    userComment: string | null;
}

interface ApiResponse {
    success: boolean;
    body: ModerationAdDetail;
}

type CustomReason = {
    id: string;
    label: string;
    value: string;
    group: ReasonGroup;
    reasonType: ReasonType;
};

const SEND_BACK_TO_MODERATORS_ALLOWED_ROLES = new Set([
    'expert',
    'expert-admin',
    'expert-outstaff',
    'expert-admin-outstaff',
    'manager-admin'
]);

const PHOTO_REQUEST_OPTIONS_BY_CATEGORY = {
    Shoes: [
        'Размерная бирка',
        'Стельки с 2-ух сторон',
        'Прошив под стелькой',
        'Язычок с 2-ух сторон',
        'Подошва',
        'Стикер на коробке',
        'Патч',
        'В лучшем качестве'
    ],
    Outerwear: [
        'Бирка все страницы',
        'Бирка у воротника (Внутри и изнутри)',
        'Фурнитура (замок, пуговицы, кнопки)',
        'Пуговица с обратно стороны и выемка на ней',
        'Патч',
        'Линза',
        'Размерная бирка',
        'Замок с 2-ух сторон',
        'В лучшем качестве'
    ],
    TopBody: [
        'Логотип',
        'Нижняя бирка все страницы',
        'Фурнитура (замок, пуговицы, кнопки)',
        'Бирка у воротника',
        'Пуговица с обратно стороны и выемка на ней',
        'Патч',
        'Линза',
        'В лучшем качестве'
    ],
    LowerBody: [
        'Застежка с 2-ух сторон',
        'Патч на задней части',
        'Пуговица с 2-ух сторон',
        'Бирка все страницы',
        'Микроэлементы',
        'В лучшем качестве'
    ],
    Accessories: [
        'Гравировка на 2-ух душках (близко)',
        'Бляшка с 2-ух сторон',
        'Штамп на коже',
        'Фурнитура и гравировка',
        'Пыльник',
        'В лучшем качестве'
    ]
} as const;

type PhotoRequestCategory = keyof typeof PHOTO_REQUEST_OPTIONS_BY_CATEGORY;

const PHOTO_REQUEST_CATEGORY_BY_VALUE: Record<string, PhotoRequestCategory> = {
    Shoes: 'Shoes',
    'Обувь': 'Shoes',
    Outerwear: 'Outerwear',
    'Верхняя одежда': 'Outerwear',
    TopBody: 'TopBody',
    'Верх': 'TopBody',
    LowerBody: 'LowerBody',
    'Низ': 'LowerBody',
    Accessories: 'Accessories',
    'Аксессуары': 'Accessories'
};

const ALL_PHOTO_REQUEST_OPTIONS = Array.from(
    new Set(Object.values(PHOTO_REQUEST_OPTIONS_BY_CATEGORY).flat())
);

const getPhotoRequestOptions = (category?: string): readonly string[] => {
    const resolvedCategory = category ? PHOTO_REQUEST_CATEGORY_BY_VALUE[category.trim()] : undefined;

    if (resolvedCategory) {
        return PHOTO_REQUEST_OPTIONS_BY_CATEGORY[resolvedCategory];
    }

    return ALL_PHOTO_REQUEST_OPTIONS;
};

export const AdExpertModerationShow = () => {
    const { id } = useParams();
    const notify = useNotify();
    const redirect = useRedirect();
    const { identity } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const canOpenUserShow = canOpenPlatformUserShow(currentUser?.role);
    const theme = useTheme();
    const { lockInfo, lockReady, lockedByAnotherUser } = useAdScreenLock('adsExpertModeration', id);
    const [adDetail, setAdDetail] = useState<ModerationAdDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [modalPhotos, setModalPhotos] = useState<string[]>([]);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [rotationDegrees, setRotationDegrees] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectLoading, setRejectLoading] = useState(false);
    const [selectedRejectReason, setSelectedRejectReason] = useState('');
    const [rejectComment, setRejectComment] = useState('');
    const [isFake, setIsFake] = useState(false);
    const [requestPhotosDialogOpen, setRequestPhotosDialogOpen] = useState(false);
    const [requestPhotosLoading, setRequestPhotosLoading] = useState(false);
    const [requestPhotosComment, setRequestPhotosComment] = useState('');
    const [sendBackDialogOpen, setSendBackDialogOpen] = useState(false);
    const [sendBackLoading, setSendBackLoading] = useState(false);
    const [sendBackComment, setSendBackComment] = useState('');
    const [selectedPhotoTypes, setSelectedPhotoTypes] = useState<string[]>([]);
    const [customPhotoTypes, setCustomPhotoTypes] = useState<string[]>([]);
    const [customPhotoTypeInput, setCustomPhotoTypeInput] = useState('');
    const [customExpertReasons, setCustomExpertReasons] = useState<CustomReason[]>([]);
    const [reasonsLoading, setReasonsLoading] = useState(false);
    const [extraInfoHelpAnchor, setExtraInfoHelpAnchor] = useState<HTMLElement | null>(null);
    const [aiResponse, setAiResponse] = useState<string | null>(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    const [aiSecondsLeft, setAiSecondsLeft] = useState(180);
    const [aiTimedOut, setAiTimedOut] = useState(false);
    const [backNavigationLoading, setBackNavigationLoading] = useState(false);
    const aiTimeoutRef = useRef<number | null>(null);
    const aiIntervalRef = useRef<number | null>(null);
    const aiAbortRef = useRef<AbortController | null>(null);
    const isActionLocked = !lockReady || lockedByAnotherUser;
    const canSendBackToModerators = SEND_BACK_TO_MODERATORS_ALLOWED_ROLES.has(currentUser?.role || '');
    const lockActionHint = 'Действия недоступны, так как объявление сейчас проверяется другим пользователем.';
    const photoRequestOptions = useMemo(
        () => [...getPhotoRequestOptions(adDetail?.info.category), 'Свой вариант'],
        [adDetail?.info.category]
    );

    const LoadingSkeleton = () => (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={2}>
                <Card>
                    <CardContent>
                        <Skeleton variant="text" width="60%" height={40} />
                        <Skeleton variant="text" width="40%" />
                        <Skeleton variant="text" width="80%" />
                    </CardContent>
                </Card>
                <Card>
                    <CardContent>
                        <Skeleton variant="text" width="50%" height={30} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="100%" />
                        </Box>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent>
                        <Skeleton variant="text" width="40%" height={30} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 2 }}>
                            <Skeleton variant="rectangular" width="100%" height={200} />
                            <Skeleton variant="rectangular" width="100%" height={200} />
                            <Skeleton variant="rectangular" width="100%" height={200} />
                        </Box>
                    </CardContent>
                </Card>
            </Stack>
        </Box>
    );

    useEffect(() => {
        if (id) {
            fetchAdDetail(id);
        }
    }, [id]);

    useEffect(() => {
        if (!adDetail || !lockReady) {
            return;
        }
        loadAiSummary(adDetail, !lockedByAnotherUser);
    }, [adDetail?.id, lockReady, lockedByAnotherUser]);

    useEffect(() => {
        let isActive = true;

        const loadReasons = async () => {
            setReasonsLoading(true);
            try {
                const result = await customReasonsProxy.getList();
                const reasons = ((result?.data as CustomReason[]) || [])
                    .filter((reason) => reason.group === 'expert')
                    .map((reason) => ({
                        ...reason,
                        reasonType: reason.reasonType ?? 'refusal'
                    }));
                if (isActive) {
                    setCustomExpertReasons(reasons);
                }
            } catch (error) {
                console.error('Failed to load custom reasons', error);
                notify('Не удалось загрузить причины', { type: 'warning' });
            } finally {
                if (isActive) {
                    setReasonsLoading(false);
                }
            }
        };

        loadReasons();

        return () => {
            isActive = false;
        };
    }, [notify]);

    useEffect(() => {
        if (!lockedByAnotherUser) {
            return;
        }
        setApproveDialogOpen(false);
        setRejectDialogOpen(false);
        setRequestPhotosDialogOpen(false);
        setSendBackDialogOpen(false);
        setSendBackLoading(false);
        setSendBackComment('');
        clearAiTimers();
        if (aiAbortRef.current) {
            aiAbortRef.current.abort();
        }
        setAiLoading(false);
    }, [lockedByAnotherUser]);

    const ensureCanModerate = () => {
        if (!lockReady) {
            notify('Проверяем, кто сейчас работает с объявлением. Попробуйте еще раз.', { type: 'warning' });
            return false;
        }
        if (lockedByAnotherUser) {
            notify(`Объявление сейчас проверяется пользователем ${lockInfo?.login || 'unknown'}`, { type: 'warning' });
            return false;
        }
        return true;
    };

    const rejectReasons = useMemo(
        () => [
            ...DEFAULT_EXPERT_REASONS,
            ...customExpertReasons.map((reason) => ({
                label: reason.label,
                value: reason.value,
                reasonType: reason.reasonType
            }))
        ].filter((reason) => reason.reasonType === 'refusal'),
        [customExpertReasons]
    );

    const buildExpertAiMessages = (detail: ModerationAdDetail) => {
        const brandModel = `${detail.info.brand || ''} ${detail.info.model || ''}`.trim();
        const photoUrls = Array.isArray(detail.info.attachmentPhotoIds)
            ? detail.info.attachmentPhotoIds.filter((url) => typeof url === 'string' && url.trim().length > 0)
            : [];

        return [
            {
                role: 'user',
                content: [
                    { type: 'text', text: `Бренд и модель: ${brandModel}` },
                    ...photoUrls.map((url) => ({
                        type: 'image_url',
                        image_url: { url }
                    }))
                ]
            }
        ];
    };

    const extractAiText = (data: any): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;

        const directKeys = [
            'message',
            'response',
            'result',
            'output',
            'answer',
            'content',
            'text',
            'markdown'
        ];

        for (const key of directKeys) {
            if (typeof data?.[key] === 'string') {
                return data[key];
            }
        }

        const choiceContent = data?.choices?.[0]?.message?.content || data?.choices?.[0]?.text;
        if (typeof choiceContent === 'string') {
            return choiceContent;
        }
        if (Array.isArray(choiceContent)) {
            const text = choiceContent
                .map((part: any) => {
                    if (typeof part === 'string') return part;
                    if (typeof part?.text === 'string') return part.text;
                    return '';
                })
                .filter(Boolean)
                .join('\n');
            if (text) {
                return text;
            }
        }

        if (Array.isArray(data)) {
            for (const item of data) {
                const extracted = extractAiText(item);
                if (extracted) return extracted;
            }
        }

        if (typeof data === 'object') {
            const nestedKeys = ['data', 'result', 'payload'];
            for (const key of nestedKeys) {
                const extracted = extractAiText(data?.[key]);
                if (extracted) return extracted;
            }
        }

        return JSON.stringify(data, null, 2);
    };

    const normalizeAiText = (value: string) => {
        if (!value) return value;
        const hasEscapedNewlines = value.includes('\\n') && !value.includes('\n');
        const hasEscapedReturns = value.includes('\\r') && !value.includes('\r');
        if (!hasEscapedNewlines && !hasEscapedReturns && !value.includes('\\t')) {
            return value;
        }
        return value
            .replace(/\\n/g, '\n')
            .replace(/\\r/g, '\r')
            .replace(/\\t/g, '\t');
    };

    const clearAiTimers = () => {
        if (aiTimeoutRef.current) {
            window.clearTimeout(aiTimeoutRef.current);
            aiTimeoutRef.current = null;
        }
        if (aiIntervalRef.current) {
            window.clearInterval(aiIntervalRef.current);
            aiIntervalRef.current = null;
        }
    };

    const formatCountdown = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const restSeconds = seconds % 60;
        return `${minutes}:${String(restSeconds).padStart(2, '0')}`;
    };

    const fetchAiResponse = async (detail: ModerationAdDetail) => {
        if (lockedByAnotherUser) {
            return;
        }

        clearAiTimers();
        if (aiAbortRef.current) {
            aiAbortRef.current.abort();
        }

        const controller = new AbortController();
        aiAbortRef.current = controller;

        setAiLoading(true);
        setAiError(null);
        setAiTimedOut(false);
        setAiResponse(null);
        setAiSecondsLeft(180);

        aiIntervalRef.current = window.setInterval(() => {
            setAiSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        aiTimeoutRef.current = window.setTimeout(() => {
            controller.abort();
            setAiTimedOut(true);
            setAiLoading(false);
        }, 180000);

        try {
            const data = await aiProxy.callLegitAgent(buildExpertAiMessages(detail), controller.signal);
            const summary = normalizeAiText(extractAiText(data));
            setAiResponse(summary);
            if (summary.trim()) {
                try {
                    await legitAiSummariesProxy.saveSummary(detail.id, summary);
                } catch (saveError) {
                    console.error('Failed to save expert AI summary', saveError);
                }
            }
        } catch (fetchError: any) {
            if (!controller.signal.aborted) {
                setAiError(fetchError?.message || 'Ошибка при запросе к AI');
            }
        } finally {
            if (!controller.signal.aborted) {
                setAiLoading(false);
            }
            clearAiTimers();
        }
    };

    useEffect(() => {
        return () => {
            clearAiTimers();
            if (aiAbortRef.current) {
                aiAbortRef.current.abort();
            }
        };
    }, []);

    const loadAiSummary = async (detail: ModerationAdDetail, allowGeneration: boolean) => {
        setAiError(null);
        setAiTimedOut(false);
        try {
            const data = await legitAiSummariesProxy.getSummary(detail.id);
            if (typeof data?.summary === 'string' && data.summary.trim()) {
                setAiResponse(data.summary);
                return;
            }
        } catch (fetchError) {
            console.error('Failed to load expert AI summary', fetchError);
        }
        if (!allowGeneration) {
            setAiResponse(null);
            return;
        }
        fetchAiResponse(detail);
    };

    const handlePhotoClick = (photos: string[], index: number) => {
        setModalPhotos(photos);
        setCurrentPhotoIndex(index);
        setPhotoModalOpen(true);
    };

    const handleCloseModal = () => {
        setPhotoModalOpen(false);
        setModalPhotos([]);
        setZoomLevel(1);
        setRotationDegrees(0);
        setIsZoomed(false);
        setImagePosition({ x: 0, y: 0 });
    };

    const handleZoomIn = () => {
        const newZoom = Math.min(zoomLevel + 0.5, 3);
        setZoomLevel(newZoom);
        setIsZoomed(newZoom > 1);
    };

    const handleZoomOut = () => {
        const newZoom = Math.max(zoomLevel - 0.5, 0.5);
        setZoomLevel(newZoom);
        setIsZoomed(newZoom > 1);
        if (newZoom <= 1) {
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const handleResetZoom = () => {
        setZoomLevel(1);
        setRotationDegrees(0);
        setIsZoomed(false);
        setImagePosition({ x: 0, y: 0 });
    };

    const handleRotateLeft = () => {
        setImagePosition({ x: 0, y: 0 });
        setRotationDegrees((prev) => prev - 90);
    };

    const handleRotateRight = () => {
        setImagePosition({ x: 0, y: 0 });
        setRotationDegrees((prev) => prev + 90);
    };

    const handleMouseDown = (event: React.MouseEvent) => {
        if (isZoomed) {
            setIsDragging(true);
            setDragStart({
                x: event.clientX - imagePosition.x,
                y: event.clientY - imagePosition.y
            });
        }
    };

    const handleMouseMove = (event: React.MouseEvent) => {
        if (isDragging && isZoomed) {
            setImagePosition({
                x: event.clientX - dragStart.x,
                y: event.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (event: React.WheelEvent) => {
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        
        const delta = event.deltaY > 0 ? -0.2 : 0.2;
        const newZoom = Math.max(0.5, Math.min(3, zoomLevel + delta));
        
        if (newZoom !== zoomLevel) {
            const zoomRatio = newZoom / zoomLevel;
            const newX = mouseX - (mouseX - imagePosition.x) * zoomRatio;
            const newY = mouseY - (mouseY - imagePosition.y) * zoomRatio;
            
            setZoomLevel(newZoom);
            setImagePosition({ x: newX, y: newY });
            setIsZoomed(newZoom > 1);
        }
    };

    const handlePreviousPhoto = () => {
        if (modalPhotos.length > 0) {
            setCurrentPhotoIndex((prev) => 
                prev === 0 ? modalPhotos.length - 1 : prev - 1
            );
            setZoomLevel(1);
            setRotationDegrees(0);
            setIsZoomed(false);
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const handleNextPhoto = () => {
        if (modalPhotos.length > 0) {
            setCurrentPhotoIndex((prev) => 
                prev === modalPhotos.length - 1 ? 0 : prev + 1
            );
            setZoomLevel(1);
            setRotationDegrees(0);
            setIsZoomed(false);
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (photoModalOpen) {
            if (event.key === 'ArrowLeft') {
                handlePreviousPhoto();
            } else if (event.key === 'ArrowRight') {
                handleNextPhoto();
            } else if (event.key === 'Escape') {
                handleCloseModal();
            } else if (event.key === '+' || event.key === '=') {
                event.preventDefault();
                handleZoomIn();
            } else if (event.key === '-') {
                event.preventDefault();
                handleZoomOut();
            } else if (event.key === '0') {
                event.preventDefault();
                handleResetZoom();
            }
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [photoModalOpen]);

    const fetchAdDetail = async (adId: string) => {
        setDetailLoading(true);
        try {
            const data: ApiResponse = await adsProxy.getExpertOne(adId);
            if (data.success) {
                setAdDetail(data.body);
            } else {
                throw new Error('Ошибка при получении данных');
            }
        } catch (error) {
            setError('Ошибка при загрузке данных объявления');
            notify('Ошибка при загрузке данных объявления', { type: 'error' });
        } finally {
            setDetailLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!ensureCanModerate()) return;
        if (!adDetail) return;
        
        setApproveLoading(true);
        try {
            await adsProxy.updateStatus(adDetail.id, {
                moderationResult: "APPROVED"
            }, { moderationType: 'EXPERT_MODERATION' });

            notify('Объявление одобрено', { type: 'success' });
            setApproveDialogOpen(false);
            redirect('list', 'adsExpertModeration');
        } catch (error) {
            console.error('Ошибка при одобрении объявления:', error);
            notify('Ошибка при одобрении объявления', { type: 'error' });
        } finally {
            setApproveLoading(false);
        }
    };

    const handleReject = async () => {
        if (!ensureCanModerate()) return;
        if (!adDetail || !selectedRejectReason) {
            notify('Выберите причину отказа', { type: 'warning' });
            return;
        }

        setRejectLoading(true);
        try {
            await adsProxy.updateStatus(adDetail.id, {
                moderationResult: "REJECT",
                publicReason: selectedRejectReason,
                internalReason: rejectComment || null,
                isFake: isFake
            }, { moderationType: 'EXPERT_MODERATION' });

            notify('Объявление отклонено', { type: 'success' });
            setRejectDialogOpen(false);
            setSelectedRejectReason('');
            setRejectComment('');
            setIsFake(false);
            redirect('list', 'adsExpertModeration');
        } catch (error) {
            console.error('Ошибка при отклонении объявления:', error);
            notify('Ошибка при отклонении объявления', { type: 'error' });
        } finally {
            setRejectLoading(false);
        }
    };

    const handleAddCustomPhotoType = () => {
        const trimmedInput = customPhotoTypeInput.trim();
        if (trimmedInput && !customPhotoTypes.includes(trimmedInput)) {
            setCustomPhotoTypes([...customPhotoTypes, trimmedInput]);
            setCustomPhotoTypeInput('');
        }
    };

    const handleRemoveCustomPhotoType = (typeToRemove: string) => {
        setCustomPhotoTypes(customPhotoTypes.filter(type => type !== typeToRemove));
    };

    const handleCustomPhotoTypeKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddCustomPhotoType();
        }
    };

    const handleRequestPhotos = async () => {
        if (!ensureCanModerate()) return;
        // Проверяем, что есть выбранные типы и если выбран "Свой вариант", то есть хотя бы один кастомный тип
        const hasValidSelections = selectedPhotoTypes.length > 0 && 
            (!selectedPhotoTypes.includes('Свой вариант') || customPhotoTypes.length > 0);
        
        if (!adDetail || !hasValidSelections) {
            notify('Выберите типы дополнительных фото', { type: 'warning' });
            return;
        }

        // Формируем итоговый текст
        let photoTypesText = selectedPhotoTypes
            .filter(type => {
                if (type === 'Свой вариант') {
                    return customPhotoTypes.length > 0;
                }
                return true;
            })
            .map(type => {
                if (type === 'Свой вариант') {
                    return customPhotoTypes.map(t => t.toLowerCase()).join(', ');
                }
                return type.toLowerCase();
            })
            .join(', ');
        
        const finalMessage = `К сожалению, по текущим фотографиям экспертам не удалось установить подлинность вещи, отправьте, пожалуйста, дополнительные фото: ${photoTypesText}`;

        setRequestPhotosLoading(true);
        try {
            await adsProxy.updateStatus(adDetail.id, {
                moderationResult: "REQUEST_ADDITIONAL_PHOTOS",
                publicReason: finalMessage,
                internalReason: requestPhotosComment || null
            }, { moderationType: 'EXPERT_MODERATION' });

            notify('Запрос на дополнительные фото отправлен', { type: 'success' });
            setRequestPhotosDialogOpen(false);
            setSelectedPhotoTypes([]);
            setCustomPhotoTypes([]);
            setCustomPhotoTypeInput('');
            setRequestPhotosComment('');
            redirect('list', 'adsExpertModeration');
        } catch (error) {
            console.error('Ошибка при запросе дополнительных фото:', error);
            notify('Ошибка при запросе дополнительных фото', { type: 'error' });
        } finally {
            setRequestPhotosLoading(false);
        }
    };

    const handleSendBackToModerators = async () => {
        if (!canSendBackToModerators) {
            notify('Недостаточно прав для этого действия', { type: 'warning' });
            return;
        }
        if (!ensureCanModerate()) return;
        if (!adDetail) return;

        setSendBackLoading(true);
        try {
            await adsProxy.sendBackToModerators(adDetail.id, sendBackComment.trim());
            notify('Объявление отправлено обратно модераторам', { type: 'success' });
            setSendBackDialogOpen(false);
            setSendBackComment('');
            redirect('list', 'adsExpertModeration');
        } catch (error) {
            console.error('Ошибка при отправке объявления обратно модераторам:', error);
            notify('Ошибка при отправке обратно модераторам', { type: 'error' });
        } finally {
            setSendBackLoading(false);
        }
    };

    const handleCloseSendBackDialog = () => {
        if (sendBackLoading) {
            return;
        }
        setSendBackDialogOpen(false);
        setSendBackComment('');
    };

    const handleBackToList = async () => {
        if (backNavigationLoading) {
            return;
        }

        setBackNavigationLoading(true);
        try {
            if (adDetail?.id) {
                await adLocksApi.release('adsExpertModeration', adDetail.id, getAdLockClientId(), { force: true });
            }
        } catch (error) {
            console.error('Failed to force release expert ad lock on back navigation', error);
            notify('Не удалось принудительно снять лок. Попробуйте обновить список.', { type: 'warning' });
        } finally {
            redirect('list', 'adsExpertModeration');
        }
    };

    if (detailLoading) {
        return (
            <Box>
                <Title title="Экспертная модерация" />
                <Box sx={{ mt: 2 }}>
                    <LoadingSkeleton />
                </Box>
            </Box>
        );
    }

    if (error) {
        notify('Ошибка при загрузке данных', { type: 'error' });
        return <div>Ошибка при загрузке данных</div>;
    }

    if (!adDetail) return <div>Данные не найдены</div>;

    const handleCopyAdId = async () => {
        try {
            await navigator.clipboard.writeText(adDetail.id);
            notify('ID объявления скопирован', { type: 'success' });
        } catch (error) {
            console.error('Failed to copy ad id', error);
            notify('Не удалось скопировать ID объявления', { type: 'warning' });
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('ru-RU');
    };
    const userIsInactive = adDetail.userInfo.isActive === false;
    const isPhotoRotated = Math.abs(rotationDegrees) % 180 === 90;
    const isDarkMode = theme.palette.mode === 'dark';
    const formatTextValue = (value: string) => value?.trim() ? value.trim() : '—';
    const formatBoolValue = (value: boolean) => (value ? 'да' : 'нет');
    const formatPriceValue = (value: number) => Number(value || 0).toLocaleString('ru-RU');
    const formatStatusLabel = (status: string) => {
        switch (status) {
            case 'MODERATION':
                return 'На модерации';
            case 'EXPERT_MODERATION':
                return 'Экспертная модерация';
            case 'APPROVED':
                return 'Одобрено';
            case 'REJECTED':
                return 'Отклонено';
            case 'NEED_EXPERT_PHOTO':
                return 'Запрошены дополнительные фото/данные';
            default:
                return status || '—';
        }
    };
    const photoUrls = adDetail.info.attachmentPhotoIds || [];
    const extraInfos = adDetail.expertModerationExtraInfos || [];
    const surfaceBorderColor = alpha(theme.palette.divider, isDarkMode ? 0.78 : 0.72);
    const cardBaseSx = {
        borderRadius: 3,
        border: '1px solid',
        borderColor: surfaceBorderColor,
        boxShadow: isDarkMode ? `0 18px 42px ${alpha(theme.palette.common.black, 0.28)}` : `0 16px 38px ${alpha(theme.palette.grey[500], 0.14)}`,
        overflow: 'hidden'
    };
    const productCardBackground = isDarkMode
        ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 52%, ${alpha(theme.palette.warning.main, 0.12)} 100%)`
        : `linear-gradient(135deg, ${alpha(theme.palette.info.light, 0.22)} 0%, ${theme.palette.background.paper} 54%, ${alpha(theme.palette.warning.light, 0.18)} 100%)`;
    const decisionPanelBackground = isDarkMode
        ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`
        : `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.info.light, 0.12)} 100%)`;
    const softSurfaceBg = isDarkMode ? alpha(theme.palette.common.white, 0.045) : alpha(theme.palette.grey[50], 0.95);
    const strongerSurfaceBg = isDarkMode ? alpha(theme.palette.common.white, 0.07) : alpha(theme.palette.common.white, 0.86);
    const photoPreviewBg = isDarkMode ? alpha(theme.palette.common.black, 0.32) : alpha(theme.palette.grey[100], 0.92);
    const aiEvidenceBg = isDarkMode ? alpha(theme.palette.info.main, 0.08) : alpha(theme.palette.info.light, 0.14);
    const metricTileSx = {
        p: 1.5,
        borderRadius: 2,
        bgcolor: softSurfaceBg,
        border: '1px solid',
        borderColor: surfaceBorderColor,
        minWidth: 0
    };
    const actionButtonSx = {
        justifyContent: 'flex-start',
        py: 1.25,
        px: 1.5,
        borderRadius: 2,
        fontWeight: 800,
        textTransform: 'none'
    };
    const currentStatusLabel = formatStatusLabel(adDetail.currentStatus);
    const userInitial = adDetail.userInfo.name?.trim().charAt(0).toUpperCase() || '?';

    return (
        <Box>
            <Title title={`Экспертная модерация: объявление ${adDetail.id}`} />
            {userIsInactive && (
                <Box
                    sx={{
                        position: 'fixed',
                        right: { xs: 12, md: 24 },
                        top: { xs: 12, md: 24 },
                        zIndex: (theme) => theme.zIndex.snackbar
                    }}
                >
                    <Alert
                        severity="error"
                        sx={{
                            width: { xs: 'calc(100vw - 24px)', sm: 520 },
                            maxWidth: '95vw',
                            boxShadow: 6,
                            borderRadius: 2
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Пользователь удален
                        </Typography>
                        <Typography variant="body1">
                            Аккаунт пользователя неактивен (удален или заблокирован).
                        </Typography>
                    </Alert>
                </Box>
            )}
            {lockedByAnotherUser && (
                <Box
                    sx={{
                        position: 'fixed',
                        right: { xs: 12, md: 24 },
                        bottom: { xs: 12, md: 24 },
                        zIndex: (theme) => theme.zIndex.snackbar
                    }}
                >
                    <Alert
                        severity="warning"
                        sx={{
                            width: { xs: 'calc(100vw - 24px)', sm: 520 },
                            maxWidth: '95vw',
                            boxShadow: 6,
                            borderRadius: 2,
                            alignItems: 'flex-start',
                            '& .MuiAlert-icon': {
                                margin: 0,
                                mr: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'flex-start',
                                mt: '2px'
                            },
                            '& .MuiAlert-message': {
                                fontSize: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start'
                            }
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Объявление занято
                        </Typography>
                        <Typography variant="body1">
                            Объявление сейчас проверяется пользователем {lockInfo?.login || 'unknown'}. Некоторые действия могут быть отключены
                        </Typography>
                    </Alert>
                </Box>
            )}
            <Box
                sx={{
                    mt: 2,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
                    gap: 2.5,
                    alignItems: 'start'
                }}
            >
                <Stack spacing={2.5} sx={{ minWidth: 0 }}>
                    <Card sx={{ ...cardBaseSx, background: productCardBackground }}>
                        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha(theme.palette.info.main, isDarkMode ? 0.18 : 0.12),
                                            color: 'info.main',
                                            flexShrink: 0
                                        }}
                                    >
                                        <Inventory2Icon />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="overline" sx={{ color: 'info.main', letterSpacing: '0.08em', fontWeight: 800 }}>
                                            Экспертная карточка
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.18, overflowWrap: 'anywhere' }}>
                                            {formatTextValue(adDetail.info.brand)} {formatTextValue(adDetail.info.model)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Chip label={currentStatusLabel} color="info" variant="outlined" />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                                    gap: 1.5
                                }}
                            >
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Цена</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>{formatPriceValue(adDetail.info.fullPrice)} ₽</Typography>
                                </Box>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Размер</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5, overflowWrap: 'anywhere' }}>{formatTextValue(adDetail.info.size)}</Typography>
                                </Box>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Состояние</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5, overflowWrap: 'anywhere' }}>{formatTextValue(adDetail.info.clothStatus)}</Typography>
                                </Box>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Локация</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5, overflowWrap: 'anywhere' }}>
                                        {formatTextValue(adDetail.info.city)}, {formatTextValue(adDetail.info.country)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    mt: 1.5,
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(0, 0.8fr)' },
                                    gap: 1.5
                                }}
                            >
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Категория</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5, overflowWrap: 'anywhere' }}>
                                        {formatTextValue(adDetail.info.category)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                                        {formatTextValue(adDetail.info.subcategory)}
                                    </Typography>
                                    {adDetail.info.serialNumber && (
                                        <Chip
                                            label={`SN: ${adDetail.info.serialNumber}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ mt: 1.25, maxWidth: '100%', '& .MuiChip-label': { overflowWrap: 'anywhere', whiteSpace: 'normal' } }}
                                        />
                                    )}
                                </Box>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Признаки</Typography>
                                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                        <Chip label={`Винтаж: ${formatBoolValue(adDetail.info.isVintage)}`} color={adDetail.info.isVintage ? 'success' : 'default'} size="small" variant={adDetail.info.isVintage ? 'filled' : 'outlined'} />
                                        <Chip label={`Коробка: ${formatBoolValue(adDetail.info.isExistBox)}`} color={adDetail.info.isExistBox ? 'success' : 'default'} size="small" variant={adDetail.info.isExistBox ? 'filled' : 'outlined'} />
                                        <Chip label={`Обмен: ${formatBoolValue(adDetail.info.isExchange)}`} color={adDetail.info.isExchange ? 'success' : 'default'} size="small" variant={adDetail.info.isExchange ? 'filled' : 'outlined'} />
                                    </Stack>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, bgcolor: strongerSurfaceBg, border: '1px solid', borderColor: surfaceBorderColor }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                        Описание продавца
                                    </Typography>
                                    {!adDetail.info.description?.trim() && <Chip label="Описание отсутствует" color="error" size="small" />}
                                </Box>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65, overflowWrap: 'anywhere' }}>
                                    {formatTextValue(adDetail.info.description)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={cardBaseSx}>
                        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                    Дополнительные данные
                                </Typography>
                                <IconButton
                                    size="small"
                                    sx={{ p: 0.25 }}
                                    onClick={(event) => setExtraInfoHelpAnchor(event.currentTarget)}
                                >
                                    <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </IconButton>
                                <Popover
                                    open={Boolean(extraInfoHelpAnchor)}
                                    anchorEl={extraInfoHelpAnchor}
                                    onClose={() => setExtraInfoHelpAnchor(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                >
                                    <Typography sx={{ p: 1.5, maxWidth: 320 }} variant="body2">
                                        Ранее запрошенные дополнительные данные (запросить можно по кнопке "Запросить фото")
                                    </Typography>
                                </Popover>
                            </Box>

                            {extraInfos.length > 0 ? (
                                <Stack spacing={2}>
                                    {extraInfos.map((extraInfo, index) => (
                                        <Box key={index} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: surfaceBorderColor, bgcolor: softSurfaceBg }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                                Запрос #{index + 1}
                                            </Typography>

                                            {extraInfo.photosUrl && extraInfo.photosUrl.length > 0 ? (
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: {
                                                            xs: 'repeat(2, minmax(0, 1fr))',
                                                            sm: 'repeat(3, minmax(0, 1fr))',
                                                            md: 'repeat(4, minmax(0, 1fr))'
                                                        },
                                                        gap: 1.25
                                                    }}
                                                >
                                                    {extraInfo.photosUrl.map((photoUrl, photoIndex) => (
                                                        <Box
                                                            key={`${index}-${photoIndex}`}
                                                            component="button"
                                                            type="button"
                                                            onClick={() => handlePhotoClick(extraInfo.photosUrl, photoIndex)}
                                                            sx={{
                                                                border: '1px solid',
                                                                borderColor: surfaceBorderColor,
                                                                borderRadius: 2,
                                                                bgcolor: photoPreviewBg,
                                                                p: 0.75,
                                                                cursor: 'pointer',
                                                                transition: 'border-color 160ms ease, box-shadow 160ms ease',
                                                                '&:hover, &:focus-visible': {
                                                                    borderColor: 'primary.main',
                                                                    boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, isDarkMode ? 0.2 : 0.12)}`,
                                                                    outline: 'none'
                                                                }
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src={photoUrl}
                                                                alt={`Дополнительное фото ${photoIndex + 1}`}
                                                                sx={{
                                                                    width: '100%',
                                                                    height: 130,
                                                                    objectFit: 'contain',
                                                                    display: 'block',
                                                                    borderRadius: 1,
                                                                    bgcolor: isDarkMode ? alpha(theme.palette.common.black, 0.22) : theme.palette.common.white
                                                                }}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    Дополнительные фото не приложены
                                                </Typography>
                                            )}

                                            <Box sx={{ mt: 1.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Комментарий пользователя
                                                </Typography>
                                                <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                                                    {extraInfo.userComment || 'Комментарий отсутствует'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Alert severity="info">Дополнительные данные еще не запрашивались.</Alert>
                            )}
                        </CardContent>
                    </Card>

                    <Card sx={cardBaseSx}>
                        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <PhotoLibraryIcon color="primary" />
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 900 }}>Фотографии товара</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Превью показываются целиком, чтобы не потерять бирки, чеки и скриншоты.
                                        </Typography>
                                    </Box>
                                </Box>
                                <Chip label={`${photoUrls.length} фото`} size="small" color={photoUrls.length > 0 ? 'primary' : 'warning'} variant="outlined" />
                            </Box>
                            {photoUrls.length > 0 ? (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, minmax(0, 1fr))',
                                            md: 'repeat(3, minmax(0, 1fr))',
                                            xl: 'repeat(4, minmax(0, 1fr))'
                                        },
                                        gap: 1.5
                                    }}
                                >
                                    {photoUrls.map((url: string, index: number) => (
                                        <Box
                                            key={index}
                                            component="button"
                                            type="button"
                                            onClick={() => handlePhotoClick(photoUrls, index)}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: surfaceBorderColor,
                                                borderRadius: 2,
                                                bgcolor: photoPreviewBg,
                                                p: 1,
                                                cursor: 'pointer',
                                                minHeight: 240,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                                transition: 'border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
                                                '&:hover, &:focus-visible': {
                                                    borderColor: 'primary.main',
                                                    boxShadow: `0 10px 26px ${alpha(theme.palette.primary.main, isDarkMode ? 0.22 : 0.16)}`,
                                                    transform: 'translateY(-2px)',
                                                    outline: 'none'
                                                }
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={url}
                                                alt={`Фото товара ${index + 1}`}
                                                sx={{
                                                    width: '100%',
                                                    height: 210,
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                    borderRadius: 1.5,
                                                    bgcolor: isDarkMode ? alpha(theme.palette.common.black, 0.22) : theme.palette.common.white
                                                }}
                                            />
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textAlign: 'left' }}>
                                                Фото {index + 1}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Alert severity="warning">Фотографии товара не найдены. Решение лучше выносить после запроса дополнительных фото.</Alert>
                            )}
                        </CardContent>
                    </Card>

                    <AiModerationResultCard aiModeration={adDetail.aiModeration} />

                    <Card sx={{ ...cardBaseSx, borderColor: alpha(theme.palette.info.main, isDarkMode ? 0.44 : 0.24) }}>
                        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                <AutoAwesomeIcon color="info" />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 900 }}>Rocket AI Legit</Typography>
                                    <Typography variant="body2" color="text.secondary">Сводка AI для проверки подлинности</Typography>
                                </Box>
                            </Box>
                            {lockedByAnotherUser && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Rocket AI Legit недоступен, пока объявление проверяется другим пользователем.
                                </Alert>
                            )}
                            {aiLoading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: aiEvidenceBg }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="body2" color="text.secondary">
                                        Формирую ответ... {formatCountdown(aiSecondsLeft)}
                                    </Typography>
                                </Box>
                            )}
                            {!aiLoading && aiError && (
                                <Alert severity="error">{aiError}</Alert>
                            )}
                            {!aiLoading && aiTimedOut && (
                                <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                    <Alert severity="warning" sx={{ flex: 1 }}>
                                        Таймаут ответа, перезагрузите страницу или запросите сводку еще раз.
                                    </Alert>
                                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                disabled={lockedByAnotherUser}
                                                onClick={() => {
                                                    if (adDetail) {
                                                        fetchAiResponse(adDetail);
                                                    }
                                                }}
                                            >
                                                Запросить еще раз
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                            {!aiLoading && !aiError && aiResponse && (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: aiEvidenceBg,
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.info.main, isDarkMode ? 0.28 : 0.18),
                                        overflowWrap: 'anywhere',
                                        '& pre': { whiteSpace: 'pre-wrap' },
                                        '& p:first-of-type': { mt: 0 },
                                        '& p:last-child': { mb: 0 }
                                    }}
                                >
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm, remarkBreaks]}
                                        components={{
                                            a: ({ node, ...props }) => (
                                                <a {...props} target="_blank" rel="noreferrer" />
                                            )
                                        }}
                                    >
                                        {aiResponse}
                                    </ReactMarkdown>
                                </Box>
                            )}
                            {!aiLoading && !aiError && !aiResponse && !aiTimedOut && (
                                <Typography variant="body2" color="text.secondary">
                                    Ответ не получен
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    <Card sx={cardBaseSx}>
                        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <HistoryIcon color="action" />
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>История статусов</Typography>
                            </Box>
                            {adDetail.statusHistory && adDetail.statusHistory.length > 0 ? (
                                <Stack spacing={1}>
                                    {adDetail.statusHistory.map((status, index) => (
                                        <Box
                                            key={`${status}-${index}`}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                p: 1.25,
                                                borderRadius: 2,
                                                bgcolor: softSurfaceBg,
                                                border: '1px solid',
                                                borderColor: surfaceBorderColor
                                            }}
                                        >
                                            <Chip label={index + 1} size="small" variant="outlined" />
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatStatusLabel(status)}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">История статусов отсутствует</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Stack>

                <Stack
                    spacing={2}
                    sx={{
                        order: { xs: -1, lg: 0 },
                        minWidth: 0,
                        position: { lg: 'sticky' },
                        top: { lg: 24 }
                    }}
                >
                    <Card
                        sx={{
                            ...cardBaseSx,
                            background: decisionPanelBackground
                        }}
                    >
                    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                        <Stack spacing={2}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToList}
                                disabled={backNavigationLoading}
                                fullWidth
                                sx={{ ...actionButtonSx, justifyContent: 'center' }}
                            >
                                {backNavigationLoading ? 'Возврат...' : 'Назад к списку'}
                            </Button>

                            <Box>
                                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em', fontWeight: 900 }}>
                                    Рабочая панель
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.18 }}>
                                    Решение эксперта
                                </Typography>
                            </Box>

                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: strongerSurfaceBg, border: '1px solid', borderColor: surfaceBorderColor }}>
                                <Typography variant="caption" color="text.secondary">Объявление</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                                    {formatTextValue(adDetail.info.brand)} {formatTextValue(adDetail.info.model)}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                                    <Chip label={`${photoUrls.length} фото`} size="small" color={photoUrls.length > 0 ? 'primary' : 'warning'} variant="outlined" />
                                    <Chip label={currentStatusLabel} size="small" color="info" variant="outlined" />
                                </Stack>
                            </Box>

                            {lockedByAnotherUser ? (
                                <Alert severity="warning">
                                    Действия недоступны: объявление проверяет {lockInfo?.login || 'unknown'}.
                                </Alert>
                            ) : !lockReady ? (
                                <Alert severity="info">Проверяем доступность объявления для работы.</Alert>
                            ) : (
                                <Alert severity="success" icon={<CheckCircleIcon />}>
                                    Объявление закреплено за вами. Можно вынести решение.
                                </Alert>
                            )}

                            <Stack spacing={1.25}>
                                <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                                    <span style={{ display: 'block' }}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            startIcon={<CheckCircleIcon />}
                                            onClick={() => setApproveDialogOpen(true)}
                                            disabled={approveLoading || isActionLocked}
                                            fullWidth
                                            sx={actionButtonSx}
                                        >
                                            Одобрить публикацию
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                                    <span style={{ display: 'block' }}>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            startIcon={<CancelIcon />}
                                            onClick={() => setRejectDialogOpen(true)}
                                            disabled={rejectLoading || isActionLocked}
                                            fullWidth
                                            sx={actionButtonSx}
                                        >
                                            Отказать
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                                    <span style={{ display: 'block' }}>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            startIcon={<PhotoLibraryIcon />}
                                            onClick={() => setRequestPhotosDialogOpen(true)}
                                            disabled={requestPhotosLoading || isActionLocked}
                                            fullWidth
                                            sx={actionButtonSx}
                                        >
                                            Запросить фото
                                        </Button>
                                    </span>
                                </Tooltip>
                                {canSendBackToModerators && (
                                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                                        <span style={{ display: 'block' }}>
                                            <Button
                                                variant="outlined"
                                                color="info"
                                                startIcon={<AssignmentReturnIcon />}
                                                onClick={() => setSendBackDialogOpen(true)}
                                                disabled={sendBackLoading || isActionLocked}
                                                fullWidth
                                                sx={actionButtonSx}
                                            >
                                                Вернуть модераторам
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </Stack>

                        </Stack>
                    </CardContent>
                </Card>
                    <Card sx={cardBaseSx}>
                        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon color={userIsInactive ? 'error' : 'success'} />
                                    <Typography variant="h6" sx={{ fontWeight: 900 }}>Пользователь</Typography>
                                </Box>
                                <Chip
                                    label={userIsInactive ? 'Удален или заблокирован' : 'Активен'}
                                    color={userIsInactive ? 'error' : 'success'}
                                    variant={userIsInactive ? 'filled' : 'outlined'}
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                {adDetail.userInfo.avatarUrl ? (
                                    <Box
                                        component="img"
                                        src={adDetail.userInfo.avatarUrl}
                                        alt="Аватар пользователя"
                                        sx={{ borderRadius: '50%', width: 56, height: 56, objectFit: 'cover' }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: '50%',
                                            bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.24 : 0.12),
                                            color: 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ fontWeight: 900 }}>{userInitial}</Typography>
                                    </Box>
                                )}
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                                        {formatTextValue(adDetail.userInfo.name)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                        {adDetail.userInfo.id ? (
                                            canOpenUserShow ? (
                                                <MuiLink
                                                    href={getPlatformUserShowUrl(adDetail.userInfo.id)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="hover"
                                                    sx={{ fontSize: 'inherit' }}
                                                >
                                                    {adDetail.userInfo.id}
                                                </MuiLink>
                                            ) : adDetail.userInfo.id
                                        ) : '—'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Рейтинг</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 900, color: adDetail.userInfo.rating >= 4 ? 'success.main' : adDetail.userInfo.rating >= 3 ? 'warning.main' : 'error.main' }}>
                                        {adDetail.userInfo.rating.toFixed(1)}
                                    </Typography>
                                </Box>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Отзывы</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{adDetail.userInfo.reviewsCount}</Typography>
                                </Box>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Активные</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>{adDetail.userInfo.countActiveAds}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={cardBaseSx}>
                        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FactCheckIcon color="info" />
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>Данные модерации</Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.25 }}>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={handleCopyAdId}
                                    sx={{
                                        ...metricTileSx,
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        transition: 'border-color 160ms ease, box-shadow 160ms ease',
                                        '&:hover, &:focus-visible': {
                                            borderColor: 'primary.main',
                                            boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, isDarkMode ? 0.2 : 0.12)}`,
                                            outline: 'none'
                                        }
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">ID объявления</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>{adDetail.id}</Typography>
                                </Box>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Статус</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{currentStatusLabel}</Typography>
                                </Box>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Создано</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatDate(adDetail.creationDate)}</Typography>
                                </Box>
                                <Box sx={metricTileSx}>
                                    <Typography variant="caption" color="text.secondary">Обновлено</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatDate(adDetail.lastUpdateDate)}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Stack>
            </Box>

            {/* Модальное окно для просмотра фотографий */}
            <Modal
                open={photoModalOpen}
                onClose={handleCloseModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 24,
                        outline: 'none',
                        overflow: 'hidden'
                    }}
                >
                    {/* Кнопка закрытия */}
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* Кнопки зума */}
                    <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1, zIndex: 1 }}>
                        <IconButton
                            onClick={handleZoomIn}
                            sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <ZoomInIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleZoomOut}
                            sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <ZoomOutIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleResetZoom}
                            sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <FullscreenIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleRotateLeft}
                            sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <RotateLeftIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleRotateRight}
                            sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <RotateRightIcon />
                        </IconButton>
                    </Box>

                    {/* Фотография */}
                    {modalPhotos.length > 0 && (
                        <Box
                            sx={{
                                width: '100%',
                                height: '95vh',
                                overflow: 'hidden',
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isZoomed ? 'grab' : 'default',
                                '&:active': {
                                    cursor: isZoomed ? 'grabbing' : 'default'
                                }
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onWheel={handleWheel}
                        >
                            <Box
                                component="img"
                                src={modalPhotos[currentPhotoIndex]}
                                alt={`Фото товара ${currentPhotoIndex + 1}`}
                                sx={{
                                    maxWidth: isPhotoRotated ? 'calc(95vh - 48px)' : 'calc(95vw - 48px)',
                                    maxHeight: isPhotoRotated ? 'calc(95vw - 48px)' : 'calc(95vh - 48px)',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    display: 'block',
                                    transition: isZoomed ? 'none' : 'transform 0.3s ease',
                                    transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${zoomLevel}) rotate(${rotationDegrees}deg)`,
                                    transformOrigin: 'center center',
                                    willChange: 'transform',
                                    userSelect: 'none',
                                    pointerEvents: 'none'
                                }}
                            />
                        </Box>
                    )}

                    {/* Кнопка "Предыдущая" */}
                    {modalPhotos.length > 1 && (
                        <IconButton
                            onClick={handlePreviousPhoto}
                            sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                zIndex: 1,
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                    )}

                    {/* Кнопка "Следующая" */}
                    {modalPhotos.length > 1 && (
                        <IconButton
                            onClick={handleNextPhoto}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                zIndex: 1,
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    )}

                    {/* Индикатор текущей фотографии */}
                    {modalPhotos.length > 1 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                gap: 1,
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: 1,
                                p: 1
                            }}
                        >
                            {modalPhotos.map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: index === currentPhotoIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setCurrentPhotoIndex(index)}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Modal>

            {/* Диалог подтверждения одобрения */}
            <Dialog
                open={approveDialogOpen}
                onClose={() => setApproveDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <CheckCircleIcon color="success" />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>Одобрить публикацию</Typography>
                            <Typography variant="body2" color="text.secondary">Подтверждение решения эксперта</Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Alert severity="success" variant="outlined" sx={{ borderRadius: 2 }}>
                        Вы уверены, что вещь является оригинальной и она может быть опубликована на платформе?
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={() => setApproveDialogOpen(false)}
                        disabled={approveLoading}
                    >
                        Нет
                    </Button>
                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                        <span>
                            <Button
                                onClick={handleApprove}
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                disabled={approveLoading || isActionLocked}
                            >
                                {approveLoading ? 'Обработка...' : 'Одобрить'}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            {/* Диалог отказа */}
            <Dialog
                open={rejectDialogOpen}
                onClose={() => setRejectDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <CancelIcon color="error" />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>Отказать в публикации</Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 900 }}>
                        Причина отказа
                    </Typography>
                    
                    <RadioGroup
                        value={selectedRejectReason}
                        onChange={(e) => setSelectedRejectReason(e.target.value)}
                        sx={{ mb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}
                    >
                        {reasonsLoading ? (
                            <Box sx={{ py: 1 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : rejectReasons.length === 0 ? (
                            <Alert severity="info">Нет причин отказа.</Alert>
                        ) : (
                            rejectReasons.map((reason) => (
                                <FormControlLabel
                                    key={`${reason.label}-${reason.value}`}
                                    value={reason.value}
                                    control={<Radio />}
                                    label={reason.label}
                                    sx={{
                                        m: 0,
                                        px: 1.5,
                                        py: 1.25,
                                        minHeight: 56,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: selectedRejectReason === reason.value ? 'error.main' : surfaceBorderColor,
                                        bgcolor: selectedRejectReason === reason.value ? alpha(theme.palette.error.main, isDarkMode ? 0.18 : 0.08) : softSurfaceBg,
                                        alignItems: 'center',
                                        gap: 1,
                                        '& .MuiRadio-root': {
                                            p: 0.5,
                                            flexShrink: 0
                                        },
                                        '& .MuiFormControlLabel-label': {
                                            fontWeight: selectedRejectReason === reason.value ? 800 : 500,
                                            lineHeight: 1.35,
                                            overflowWrap: 'anywhere'
                                        }
                                    }}
                                />
                            ))
                        )}
                    </RadioGroup>

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 900 }}>
                        Внутренний комментарий
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="Введите дополнительный комментарий..."
                        value={rejectComment}
                        onChange={(e) => setRejectComment(e.target.value)}
                    />
                    <Alert
                        severity="info"
                        icon={<InfoIcon />}
                        sx={{ mt: 2, borderRadius: 2 }}
                    >
                        <Typography variant="body2">
                            Дополнительный комментарий не будет виден пользователю и используется только для внутренней аналитики
                        </Typography>
                    </Alert>

                    <Box sx={{ mt: 2, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: isFake ? alpha(theme.palette.error.main, 0.5) : surfaceBorderColor, bgcolor: isFake ? alpha(theme.palette.error.main, isDarkMode ? 0.18 : 0.08) : softSurfaceBg }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isFake}
                                    onChange={(e) => setIsFake(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Это фейковое объявление"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={() => {
                            setRejectDialogOpen(false);
                            setSelectedRejectReason('');
                            setRejectComment('');
                            setIsFake(false);
                        }}
                        disabled={rejectLoading}
                    >
                        Отмена
                    </Button>
                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                        <span>
                            <Button
                                onClick={handleReject}
                                variant="contained"
                                color="error"
                                startIcon={<CancelIcon />}
                                disabled={rejectLoading || !selectedRejectReason || isActionLocked}
                            >
                                {rejectLoading ? 'Обработка...' : 'Отказать'}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            <Dialog
                open={sendBackDialogOpen}
                onClose={handleCloseSendBackDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <AssignmentReturnIcon color="info" />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>Вернуть модераторам</Typography>
                            <Typography variant="body2" color="text.secondary">Объявление уйдет на обычную модерацию</Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Вы точно уверены, что хотите вернуть это объявление на обычную модерацию?
                    </Typography>
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        Используйте это действие только если модератор допустил ошибку, и объявление нужно вернуть ему обратно.
                    </Alert>
                    <TextField
                        label="Комментарий для модератора"
                        placeholder="Опишите, что нужно проверить или исправить"
                        value={sendBackComment}
                        onChange={(event) => setSendBackComment(event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        disabled={sendBackLoading}
                        inputProps={{ maxLength: 1000 }}
                        helperText={`${sendBackComment.length}/1000 · необязательно`}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={handleCloseSendBackDialog}
                        disabled={sendBackLoading}
                    >
                        Не отправлять
                    </Button>
                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                        <span>
                            <Button
                                onClick={handleSendBackToModerators}
                                variant="contained"
                                color="info"
                                startIcon={<AssignmentReturnIcon />}
                                disabled={sendBackLoading || isActionLocked}
                            >
                                {sendBackLoading ? 'Отправка...' : 'Отправить обратно модераторам'}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            {/* Диалог запроса дополнительных фото */}
            <Dialog
                open={requestPhotosDialogOpen}
                onClose={() => setRequestPhotosDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <PhotoLibraryIcon color="warning" />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>Запросить дополнительные фото</Typography>
                            <Typography variant="body2" color="text.secondary">Выберите, какие доказательства нужны для проверки</Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 900 }}>
                        Типы дополнительных фото
                    </Typography>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1, mb: 3 }}>
                        {photoRequestOptions.map((type) => (
                            <FormControlLabel
                                key={type}
                                control={
                                    <Checkbox
                                        checked={selectedPhotoTypes.includes(type)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedPhotoTypes([...selectedPhotoTypes, type]);
                                            } else {
                                                setSelectedPhotoTypes(selectedPhotoTypes.filter(t => t !== type));
                                            }
                                        }}
                                    />
                                }
                                label={type}
                                sx={{
                                    m: 0,
                                    p: 1.1,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: selectedPhotoTypes.includes(type) ? 'warning.main' : surfaceBorderColor,
                                    bgcolor: selectedPhotoTypes.includes(type) ? alpha(theme.palette.warning.main, isDarkMode ? 0.18 : 0.08) : softSurfaceBg,
                                    '& .MuiFormControlLabel-label': {
                                        fontWeight: selectedPhotoTypes.includes(type) ? 800 : 500,
                                        overflowWrap: 'anywhere'
                                    }
                                }}
                            />
                        ))}
                    </Box>

                    {selectedPhotoTypes.includes('Свой вариант') && (
                        <Box sx={{ mb: 3, p: 1.5, borderRadius: 2, bgcolor: softSurfaceBg, border: '1px solid', borderColor: surfaceBorderColor }}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Введите свой вариант..."
                                    value={customPhotoTypeInput}
                                    onChange={(e) => setCustomPhotoTypeInput(e.target.value)}
                                    onKeyPress={handleCustomPhotoTypeKeyPress}
                                />
                                <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                                    <span>
                                        <IconButton
                                            onClick={handleAddCustomPhotoType}
                                            disabled={!customPhotoTypeInput.trim() || isActionLocked}
                                            sx={{ 
                                                bgcolor: 'primary.main', 
                                                color: 'white', 
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%'
                                            }}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                            {customPhotoTypes.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {customPhotoTypes.map((type, index) => (
                                        <Chip
                                            key={index}
                                            label={type}
                                            onDelete={() => handleRemoveCustomPhotoType(type)}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Предварительный просмотр */}
                    {selectedPhotoTypes.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 900 }}>
                                Итоговый комментарий, который увидит пользователь:
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                value={(() => {
                                    let photoTypesText = selectedPhotoTypes
                                        .filter(type => {
                                            if (type === 'Свой вариант') {
                                                return customPhotoTypes.length > 0;
                                            }
                                            return true;
                                        })
                                        .map(type => {
                                            if (type === 'Свой вариант') {
                                                return customPhotoTypes.map(t => t.toLowerCase()).join(', ');
                                            }
                                            return type.toLowerCase();
                                        })
                                        .join(', ');
                                    return `К сожалению, по текущим фотографиям экспертам не удалось установить подлинность вещи, отправьте, пожалуйста, дополнительные фото: ${photoTypesText}`;
                                })()}
                                InputProps={{
                                    readOnly: true,
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        backgroundColor: strongerSurfaceBg,
                                        borderRadius: 2,
                                        '& .MuiInputBase-input': {
                                            color: 'text.primary'
                                        }
                                    }
                                }}
                            />
                        </Box>
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 900 }}>
                        Внутренний комментарий
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="Введите дополнительный комментарий..."
                        value={requestPhotosComment}
                        onChange={(e) => setRequestPhotosComment(e.target.value)}
                    />
                    <Alert
                        severity="info"
                        icon={<InfoIcon />}
                        sx={{ mt: 2, borderRadius: 2 }}
                    >
                        <Typography variant="body2">
                            Дополнительный комментарий не будет виден пользователю и используется только для внутренней аналитики
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={() => {
                            setRequestPhotosDialogOpen(false);
                            setSelectedPhotoTypes([]);
                            setCustomPhotoTypes([]);
                            setCustomPhotoTypeInput('');
                            setRequestPhotosComment('');
                        }}
                        disabled={requestPhotosLoading}
                    >
                        Отмена
                    </Button>
                    <Tooltip title={lockActionHint} disableHoverListener={!lockedByAnotherUser}>
                        <span>
                            <Button
                                onClick={handleRequestPhotos}
                                variant="contained"
                                color="warning"
                                startIcon={<PhotoLibraryIcon />}
                                disabled={requestPhotosLoading || isActionLocked || selectedPhotoTypes.length === 0 || (selectedPhotoTypes.includes('Свой вариант') && customPhotoTypes.length === 0)}
                            >
                                {requestPhotosLoading ? 'Обработка...' : 'Отправить запрос'}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>
        </Box>
    );
}; 
